/**
 * @file index.tsx
 *
 * @fileoverview Shows organization contacts when in a view mode.
 */
import { FormattedMessage, useIntl } from 'react-intl';
import { LinkEmail } from '~/jobiqo/components/Link/Email';
import { LinkPhone } from '~/jobiqo/components/Link/Phone';
import { OrganizationWidgetsBoxLabel } from '~/jobiqo/components/Organization/Widgets/BoxLabel';
import { OrganizationContactsWidgetEmpty } from '~/jobiqo/components/Organization/Widgets/Contacts/Empty';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { OrganizationContactPeopleToggle } from '~/jobiqo/components/Slots/OrganizationContactPeopleToggle';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Box } from '~/vcl/atoms/07-box';
import { Avatar } from '~/vcl/atoms/12-user/01-avatar';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { EnvelopeIcon } from '~/vcl/icons/Envelope';
import { LinkedinIcon } from '~/vcl/icons/Linkedin';

export interface OrganizationContactsWidgetViewProps {
  /**
   * The current organization profile.
   */
  organization: OrganizationProfile;
  /**
   * The current box id.
   */
  boxId: string | undefined;
}

export const OrganizationContactsWidgetView = ({
  organization,
  boxId,
}: OrganizationContactsWidgetViewProps) => {
  const intl = useIntl();
  const { EMAIL } = getCommonTranslations();
  if (!organization?.contactPersons?.length) {
    return (
      <OrganizationContactsWidgetEmpty
        organization={organization}
        boxId={boxId}
      />
    );
  }

  return (
    <Box
      label={
        <OrganizationWidgetsBoxLabel organization={organization} boxId={boxId}>
          {intl.formatMessage({
            id: 'organization_contacts_label',
            defaultMessage: 'Get in touch',
          })}
        </OrganizationWidgetsBoxLabel>
      }
    >
      {organization?.contactPersons?.map((contact, index) => {
        const contactPersonTitle = `${contact?.firstName} ${contact?.lastName}${
          contact?.academicPrefix ? ', ' + contact.academicPrefix : ''
        }${contact?.academicPostfix ? ', ' + contact.academicPostfix : ''} `;

        return (
          <FlexItem key={index} marginBottom={7}>
            <FlexItems flexWrap="nowrap">
              <FlexItem flex="0 0 auto" marginRight={5}>
                <Avatar
                  alt={`${contactPersonTitle} picture`}
                  title={contactPersonTitle}
                  {...(contact?.photo?.url
                    ? { src: contact?.photo?.url }
                    : null)}
                />
              </FlexItem>
              <FlexItem>
                {(contact?.firstName || contact?.lastName) && (
                  <>
                    <Text tag="h3" fontSize={3} color="gray8" fontWeight="bold">
                      {contactPersonTitle}
                    </Text>
                  </>
                )}
                {contact?.position && (
                  <>
                    <Text fontSize={3} color="gray6" marginTop={0}>
                      {contact?.position}
                    </Text>
                    <Spacing />
                  </>
                )}
                <OrganizationContactPeopleToggle
                  contact={contact}
                  organization={organization}
                >
                  {contact?.phone && <LinkPhone phone={contact.phone} />}
                  {contact?.email && (
                    <FlexItems gap={4} alignItems="center">
                      <EnvelopeIcon color="primary" />
                      <LinkEmail email={contact.email} text={EMAIL} />
                      <Spacing />
                    </FlexItems>
                  )}
                  {contact?.linkedinProfile && (
                    <>
                      <FlexItems alignItems="center" gap={4}>
                        <LinkedinIcon title="Office" color="primary" />
                        <Text margin={0} color="gray8">
                          <a
                            href={contact?.linkedinProfile}
                            target="_blank"
                            referrerPolicy="no-referrer"
                            rel="noopener noreferrer"
                          >
                            <FormattedMessage
                              id="organization_linkedin_label"
                              defaultMessage="LinkedIn"
                            />
                          </a>
                        </Text>
                      </FlexItems>
                      <Spacing />
                    </>
                  )}
                </OrganizationContactPeopleToggle>
              </FlexItem>
              <FlexItem flex="0 0 auto">
                <EditLink
                  organization={organization}
                  boxId={boxId}
                  fieldIndex={index}
                  boxHasTitle={true}
                  {...(contact?.id && {
                    entityId: contact.id,
                  })}
                />
              </FlexItem>
            </FlexItems>
            {contact?.contactInfo && (
              <>
                <Text color="gray4" marginTop="0">
                  {contact?.contactInfo}
                </Text>
              </>
            )}
            <Spacing size="medium" />
          </FlexItem>
        );
      })}
    </Box>
  );
};
